/**
 * What every fetch in the SPA shares: the error type a failed response becomes,
 * the guarded storage accessor, and the key an owner token is kept under.
 *
 * The client itself — the `request` function and the typed `api` object — lives
 * in `app/api.ts`. This file has no fetch of its own, so the scene core can
 * recognise an `ApiError` and read an owner token without depending on the
 * application layer.
 */
export function ownerTokenKey(calendarId: string): string {
  return `circle:owner:${calendarId}`;
}

/** localStorage is absent in SSR and throws in some privacy modes. */
export function safeStorage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

export function readOwnerToken(calendarId: string, storage = safeStorage()): string | null {
  try {
    return storage?.getItem(ownerTokenKey(calendarId)) ?? null;
  } catch {
    return null;
  }
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  /** Seconds from a 429's Retry-After, when the server sent one. */
  readonly retryAfter: number | undefined;

  constructor(status: number, code: string, message: string, retryAfter?: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.retryAfter = retryAfter;
  }

  /** True when the caller should offer a way to sign in or present a token. */
  get isAuth(): boolean {
    return this.status === 401 || this.status === 403;
  }
}

/**
 * Codes whose message tells the reader nothing. "Internal error" is the
 * worker's last resort and belongs in a log; anything that would repeat it to a
 * person has to say what was being attempted instead.
 */
const OPAQUE_CODES = new Set(['INTERNAL', 'http_500', 'http_502', 'http_503', 'http_504']);

export function isOpaqueError(err: unknown): boolean {
  return err instanceof ApiError && OPAQUE_CODES.has(err.code);
}

/** Reads a JSON error envelope into an ApiError. Never throws. */
export async function toApiError(response: Response): Promise<ApiError> {
  let code = `http_${response.status}`;
  let message = response.statusText || 'request failed';
  try {
    const payload = (await response.json()) as { error?: { code?: string; message?: string } };
    if (payload?.error?.code) code = payload.error.code;
    if (payload?.error?.message) message = payload.error.message;
  } catch {
    /* non-JSON error body */
  }
  const retry = Number(response.headers.get('retry-after'));
  return new ApiError(
    response.status,
    code,
    message,
    Number.isFinite(retry) && retry > 0 ? retry : undefined,
  );
}
